import React, { useEffect, useState } from 'react';
import { CreateAptTagResponse, CreateTagBody } from '../../api/createAptTag';
import { track } from '../../tracking';

type TagFormState = {
    label: string;
    hex: string;
    bg_hex: string;
    isLoading: boolean;
    error: string;
};

export type TagFormPropsType = {
    closeForm: () => void;
    onAdditionOfTag: (tagRecord: CreateTagBody, aid?: string) => CreateAptTagResponse;
    aid: string;
};

export default function CreateTagsForm({
    closeForm,
    onAdditionOfTag,
    aid,
}: TagFormPropsType): JSX.Element {
    useEffect(() => {
        track('CREATE_TAG_FORM_VIEW');
    }, []);

    const [formState, setFormState] = useState<TagFormState>({
        label: '',
        hex: '',
        bg_hex: '',
        error: '',
        isLoading: false,
    });

    const colorsToBeChosen = [
        {
            textColor: '#D75C86',
            backgroundColor: '#F2DEE5',
        },
        {
            textColor: '#EC6657',
            backgroundColor: '#FFEAE3',
        },
        {
            textColor: '#A624A0',
            backgroundColor: '#FFECFE',
        },
        {
            textColor: '#CE9A12',
            backgroundColor: '#FFF5DB',
        },
        {
            textColor: '#30AC41',
            backgroundColor: '#E3F8E6',
        },
        {
            textColor: '#149CFE',
            backgroundColor: '#E6F4FE',
        },
        {
            textColor: '#515BA7',
            backgroundColor: '#E5E7F3',
        },
    ];

    const isDisabled =
        !formState.label.trim() ||
        !formState.hex ||
        !formState.bg_hex ||
        formState.isLoading ||
        !!formState.error;

    return (
        <div className="flex flex-col justify-between font-lato h-full min-w-360 max-w-360">
            <div className="flex flex-col w-full h-full">
                <div className="flex justify-between px-24 py-16 items-center border-b border-background-secondary">
                    <p className="font-700 text-14">Create your own tag</p>
                    <svg
                        onClick={closeForm}
                        className={'cursor-pointer'}
                        width="9"
                        height="10"
                        viewBox="0 0 9 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M8.65625 8.00391C8.90234 8.27734 8.90234 8.6875 8.65625 8.93359C8.38281 9.20703 7.97266 9.20703 7.72656 8.93359L4.5 5.67969L1.24609 8.93359C0.972656 9.20703 0.5625 9.20703 0.316406 8.93359C0.0429688 8.6875 0.0429688 8.27734 0.316406 8.00391L3.57031 4.75L0.316406 1.49609C0.0429688 1.22266 0.0429688 0.8125 0.316406 0.566406C0.5625 0.292969 0.972656 0.292969 1.21875 0.566406L4.5 3.84766L7.75391 0.59375C8 0.320312 8.41016 0.320312 8.65625 0.59375C8.92969 0.839844 8.92969 1.25 8.65625 1.52344L5.40234 4.75L8.65625 8.00391Z"
                            fill="#4C5980"
                        />
                    </svg>
                </div>
                <div className="px-16 py-18 flex flex-col space-y-28">
                    <div className="flex flex-col">
                        <p className="font-700 text-14">
                            Tag Name<sup className="text-status-warning">*</sup>
                        </p>
                        <div
                            className={`px-14 pt-15 pb-13 border ${
                                !formState.error
                                    ? 'border-darwin-neutral-200'
                                    : 'border-status-error'
                            } rounded-8 mt-8`}
                        >
                            <input
                                type={'text'}
                                className={'outline-none w-full'}
                                value={formState.label}
                                onChange={(e) => {
                                    setFormState((prevValue) => ({
                                        ...prevValue,
                                        label: e.target.value?.toUpperCase(),
                                        error: '',
                                    }));
                                }}
                            />
                        </div>
                        {!!formState.error ? (
                            <p className="mt-4 text-status-error text-14 font-400">
                                {formState.error}
                            </p>
                        ) : null}
                    </div>
                    <div className="flex flex-col">
                        <p className="font-700 text-14">
                            Select Colour<sup className="text-status-warning">*</sup>
                        </p>
                        <div className="flex flex-wrap">
                            {colorsToBeChosen.map(({ textColor, backgroundColor }, idx) => {
                                const isSelected = textColor === formState.hex;
                                return (
                                    <div
                                        onClick={() => {
                                            setFormState((prevValue) => ({
                                                ...prevValue,
                                                bg_hex: backgroundColor,
                                                hex: textColor,
                                                error: '',
                                            }));
                                        }}
                                        key={`${idx}`}
                                        style={{ backgroundColor: textColor }}
                                        className={`w-24 h-24 rounded-2 text-white flex items-center justify-center mt-16 ${
                                            idx === colorsToBeChosen.length - 1 ? '' : 'mr-24'
                                        }`}
                                    >
                                        {isSelected ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                aria-hidden="true"
                                                focusable="false"
                                                data-prefix="far"
                                                data-icon="check"
                                                className="svg-inline--fa fa-check fa-w-16 h-18 w-18 mx-auto"
                                                role="img"
                                                viewBox="0 0 512 512"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z"
                                                />
                                            </svg>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex w-full bg-white pt-10 pb-18 px-16">
                <button
                    disabled={isDisabled}
                    onClick={async () => {
                        setFormState((prevState) => ({ ...prevState, isLoading: true }));
                        const {
                            success,
                            error = success ? '' : 'Failed to create this tag',
                        } = await onAdditionOfTag(
                            {
                                label: formState.label,
                                hex: formState.hex,
                                bg_hex: formState.bg_hex,
                            },
                            aid,
                        );
                        setFormState((prevState) => ({
                            ...prevState,
                            isLoading: false,
                            error,
                        }));
                        // track('SUBMIT_TAG_FORM', {
                        //     success: `${success}`,
                        //     error,
                        // });
                        if (success) {
                            closeForm();
                        }
                    }}
                    className={`rounded-8 w-full py-14 font-700 text-14 w-full flex justify-center bg-darwin-primary-500 text-white ${
                        isDisabled ? 'bg-opacity-30' : ''
                    }`}
                >
                    {formState.isLoading ? (
                        <div className="flex items-center justify-center w-24 h-24">
                            <svg
                                className="w-24 h-24 fill-white animate-spin"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M512 256c0 36.59-7.83 71.34-21.77 102.8c-5.834 13.17-21.64 18.65-34.15 11.5c-10.5-5.996-15.06-18.94-10.12-29.98C457.5 314.6 464 286 464 256c0-107.7-82.26-196.5-187.2-206.1C264.8 47.84 256 37.34 256 25.36c0-14.52 12.8-25.45 27.23-23.92C411.6 15.08 512 124 512 256z"></path>
                            </svg>
                        </div>
                    ) : (
                        <>Done</>
                    )}
                </button>
            </div>
        </div>
    );
}
